import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import { InternalSplitData, ModuleSplitRow, PRODUCT_DISPLAY_NAMES } from '../utils/pricingSheetData';
import { BuildupStepsTable, LineBuildup, ProjectLevelSection } from './InternalSplitBuildupViews';
import logo from '../../../assets/images/CRCEvansLogo.png';

// Shown once per module: what the onboarding (NRE) line buys and when it applies.
const ONBOARDING_NOTE =
  'Onboarding (NRE): one-time engineering to automate data ingestion for this ' +
  'source (build the parser for its formats, map its fields, validate to target ' +
  'accuracy). Charged once per new source; skipped for already-onboarded sources.';

const BRAND_RED = '#EB1C37';
const TEXT_DARK = '#00273F';
const TEXT_GRAY = '#464646';
const BORDER_GRAY = '#E2E5EA';
const BG_LIGHT = '#F5F6F8';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: TEXT_DARK,
    paddingTop: 80,
    paddingBottom: 52,
    paddingHorizontal: 42,
  },
  // ── Header ── (fixed, repeats every page: full on page 1, compact after)
  headerBand: {
    position: 'absolute',
    top: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: `2px solid ${BRAND_RED}`,
  },
  logoImage: { width: 130, height: 20 },
  logoImageSmall: { width: 95, height: 15 },
  runningHeaderText: { fontSize: 8, color: TEXT_GRAY },
  proposalTitleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'baseline',
  },
  proposalTitleBase: { fontSize: 15, fontFamily: 'Helvetica-Bold' },
  internalBadge: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    backgroundColor: '#969696',
    paddingHorizontal: 5,
    paddingVertical: 2,
    marginLeft: 6,
    borderRadius: 3,
  },
  proposalMeta: {
    fontSize: 8,
    color: TEXT_GRAY,
    textAlign: 'right',
    marginTop: 4,
  },
  // ── Two-column info ──
  infoRow: { flexDirection: 'row', marginBottom: 16 },
  infoBox: { flex: 1 },
  infoBoxRight: { flex: 1, paddingLeft: 24 },
  sectionHeading: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottom: `1px solid ${BRAND_RED}`,
  },
  infoLine: { flexDirection: 'row', marginBottom: 3 },
  infoLabel: { width: 72, fontSize: 8, color: TEXT_GRAY },
  infoValue: { flex: 1, fontSize: 8, color: TEXT_DARK },
  // ── Cost summary ──
  summaryBox: {
    padding: 12,
    backgroundColor: BG_LIGHT,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 10,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  summaryLabel: { fontSize: 9, color: TEXT_GRAY },
  summaryLabelBold: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  summaryValue: { fontSize: 9, color: TEXT_DARK, textAlign: 'right' },
  summaryValueBold: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    textAlign: 'right',
  },
  summaryValueRed: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textAlign: 'right',
  },
  summaryLabelRed: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
  },
  summaryDivider: {
    borderTop: `1px solid ${BORDER_GRAY}`,
    marginVertical: 7,
  },
  summaryDoubleDivider: {
    borderTop: `2px solid ${BRAND_RED}`,
    marginVertical: 6,
  },
  indentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    paddingLeft: 10,
  },
  // Per-module one-time grouping
  moduleGroupLabel: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    marginTop: 12,
    marginBottom: 4,
  },
  // Per-mill grouping
  millNameLabel: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    marginTop: 12,
    marginBottom: 3,
  },
  onboardingNoteRow: { flexDirection: 'row', marginTop: 4, marginBottom: 2 },
  onboardingNote: { flex: 1, fontSize: 8, color: TEXT_GRAY, fontStyle: 'italic', lineHeight: 1.35 },
  // Welding breakdown: per-line derivation note and the units footer.
  weldingCalcRow: { paddingLeft: 10, marginTop: -1, marginBottom: 2 },
  weldingCalc: { fontSize: 8, color: '#969696' },
  weldingFooterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    paddingTop: 6,
    borderTop: `1px solid ${BORDER_GRAY}`,
  },
  weldingFooter: { fontSize: 8, color: '#969696', marginRight: 14 },
  // Module cost summary: per-module cost lines that sum to the module cost,
  // each with its derivation note, then the cost-vs-price-vs-margin footer.
  costModuleHeading: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    marginTop: 12,
    marginBottom: 3,
  },
  costLineRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 10,
    marginBottom: 2.5,
  },
  costLineLabel: { fontSize: 9, color: TEXT_GRAY },
  costLineValue: { fontSize: 9, color: TEXT_DARK, textAlign: 'right' },
  costLineNote: { fontSize: 8, color: '#969696', paddingLeft: 10, marginBottom: 3 },
  costModuleTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `1px solid ${BORDER_GRAY}`,
    paddingTop: 3,
    marginTop: 3,
  },
  costModuleTotalLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: TEXT_DARK },
  costModuleTotalValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: TEXT_DARK,
    textAlign: 'right',
  },
  costModuleMeta: { fontSize: 8, color: '#969696', textAlign: 'right', marginTop: 1 },
  costGrandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `2px solid ${BRAND_RED}`,
    paddingTop: 5,
    marginTop: 6,
  },
  costGrandLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND_RED },
  costGrandValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_RED,
    textAlign: 'right',
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 42,
    right: 42,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `1px solid ${BORDER_GRAY}`,
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: '#969696' },
});

const formatCurrency = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InfoLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={styles.infoLine}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value}</Text>
  </View>
);

const SummaryRow: React.FC<{
  label: string;
  value: number;
  bold?: boolean;
  red?: boolean;
  indent?: boolean;
}> = ({ label, value, bold, red, indent }) => (
  <View style={indent ? styles.indentRow : styles.summaryRow}>
    <Text style={red ? styles.summaryLabelRed : bold ? styles.summaryLabelBold : styles.summaryLabel}>
      {label}
    </Text>
    <Text style={red ? styles.summaryValueRed : bold ? styles.summaryValueBold : styles.summaryValue}>
      {formatCurrency(value)}
    </Text>
  </View>
);

const ModuleSplitSection: React.FC<{ module: ModuleSplitRow }> = ({ module }) => {
  const hasMills = module.mills.length > 0;
  return (
    <View wrap={false}>
      <Text style={styles.sectionHeading}>
        {module.productName} Module
      </Text>
      <View style={styles.summaryBox}>
        {hasMills && (
          <View style={styles.onboardingNoteRow}>
            <Text style={styles.onboardingNote}>{ONBOARDING_NOTE}</Text>
          </View>
        )}

        {module.mills.map((mill, mi) => (
          <View key={mi}>
            <Text style={styles.millNameLabel}>{mill.name}</Text>
            {mill.lines.map((line, li) => (
              <View key={li}>
                <SummaryRow label={line.label} value={line.amount} indent />
                {line.buildupSteps && line.buildupSteps.length > 0 ? (
                  <BuildupStepsTable steps={line.buildupSteps} />
                ) : (
                  <LineBuildup detail={line.detail} />
                )}
              </View>
            ))}
          </View>
        ))}

        {module.other.length > 0 && (
          <>
            {hasMills && <View style={styles.summaryDivider} />}
            {module.other.map((line, i) => (
              <View key={i}>
                <SummaryRow label={line.label} value={line.amount} indent />
                {line.buildupSteps && line.buildupSteps.length > 0 ? (
                  <BuildupStepsTable steps={line.buildupSteps} />
                ) : (
                  <LineBuildup detail={line.detail} />
                )}
              </View>
            ))}
          </>
        )}

        <View style={styles.summaryDivider} />
        <SummaryRow label="One-Time Sub-Total" value={module.oneTime.subtotal} bold />

        <View style={styles.summaryDoubleDivider} />
        <SummaryRow label={`${module.productName} Total`} value={module.total} red />
      </View>
    </View>
  );
};

// Optional small calc note shown under an indented line, matching the in-app
// welding breakdown panel (e.g. "35 units x $475.00/mo x 24 mo").
const WeldingCalcNote: React.FC<{ calc: string }> = ({ calc }) =>
  calc ? (
    <View style={styles.weldingCalcRow}>
      <Text style={styles.weldingCalc}>{calc}</Text>
    </View>
  ) : null;

// Welding renders in the same module breakdown the welding tab shows: aggregate
// hardware, platform licence and mobilisation into the base price, then the
// recurring other costs, then the welding total. Every figure comes from the
// calculate-price response already carried on InternalSplitData; nothing is
// recomputed here beyond the per-line derivation notes.
const WeldingSplitSection: React.FC<{ data: InternalSplitData }> = ({ data }) => {
  const months = data.durationMonths;
  const machines = data.weldingMachines;
  const o = data.otherCosts;
  const discount = data.discountPercentage;

  const totalUnits = machines.reduce((s, m) => s + m.actualUnits + m.spareUnits, 0);
  const swUnits = machines.reduce((s, m) => s + m.swChargeableUnits, 0);
  const actualUnits = machines.reduce((s, m) => s + m.actualUnits, 0);
  const spareUnits = machines.reduce((s, m) => s + m.spareUnits, 0);

  // Effective per-unit-per-month and per-month rates, recovered from the line
  // totals so the notes show the same arithmetic as the welding tab.
  const perUnitMonth = (total: number, units: number): string =>
    total && units > 0 && months > 0
      ? `${units} units x ${formatCurrency(total / (units * months))}/mo x ${months} mo`
      : '';
  const perMonth = (total: number): string =>
    total && months > 0 ? `${formatCurrency(total / months)}/mo x ${months} mo` : '';
  const withDiscount = (base: string): string =>
    base && discount > 0 ? `${base} (after ${discount}% discount)` : base;

  const hardwareCalc = withDiscount(perUnitMonth(data.hardwareCost, totalUnits));
  const softwareCalc = withDiscount(perUnitMonth(data.softwareCost, swUnits));
  const hasOther =
    [o.internet, o.cloud, o.remoteSupport, o.training, o.provisioning, o.onFieldSupport].some(
      (v) => (v ?? 0) > 0
    );

  return (
    <View wrap={false}>
      <Text style={styles.sectionHeading}>Welding Module</Text>
      <View style={styles.summaryBox}>
        <SummaryRow label="Hardware" value={data.hardwareCost} indent />
        <WeldingCalcNote calc={hardwareCalc} />
        <SummaryRow label="DATA 360 Platform License" value={data.softwareCost} indent />
        <WeldingCalcNote calc={softwareCalc} />
        <SummaryRow label="Mobilization" value={data.mobilizationCost} indent />

        <View style={styles.summaryDivider} />
        <SummaryRow label="Base Price" value={data.basePrice} bold />

        {hasOther && (
          <>
            <View style={styles.summaryDivider} />
            {o.internet > 0 && (
              <>
                <SummaryRow label="Internet" value={o.internet} indent />
                <WeldingCalcNote calc={perMonth(o.internet)} />
              </>
            )}
            {o.cloudCompute > 0 || o.cloudStorage > 0 ? (
              <>
                {o.cloudCompute > 0 && (
                  <>
                    <SummaryRow label="Cloud compute" value={o.cloudCompute} indent />
                    <WeldingCalcNote calc={perMonth(o.cloudCompute)} />
                  </>
                )}
                {o.cloudStorage > 0 && (
                  <>
                    <SummaryRow label="Cloud storage" value={o.cloudStorage} indent />
                    <WeldingCalcNote calc={perMonth(o.cloudStorage)} />
                  </>
                )}
              </>
            ) : (
              o.cloud > 0 && (
                <>
                  <SummaryRow label="Cloud" value={o.cloud} indent />
                  <WeldingCalcNote calc={perMonth(o.cloud)} />
                </>
              )
            )}
            {o.remoteSupport > 0 && (
              <>
                <SummaryRow label="Remote Support" value={o.remoteSupport} indent />
                <WeldingCalcNote calc={perMonth(o.remoteSupport)} />
              </>
            )}
            {o.training > 0 && <SummaryRow label="Training" value={o.training} indent />}
            {o.provisioning > 0 && <SummaryRow label="Provisioning" value={o.provisioning} indent />}
            {o.onFieldSupport > 0 && (
              <SummaryRow label="On-Field Support" value={o.onFieldSupport} indent />
            )}
            <SummaryRow label="Total Other Costs" value={data.totalOtherCosts} bold />
          </>
        )}

        <View style={styles.summaryDoubleDivider} />
        <SummaryRow label="Welding Total" value={data.weldingTotal} red />

        <View style={styles.weldingFooterRow}>
          <Text style={styles.weldingFooter}>Duration: {months} months</Text>
          <Text style={styles.weldingFooter}>Total units: {totalUnits}</Text>
          <Text style={styles.weldingFooter}>Actual: {actualUnits}</Text>
          <Text style={styles.weldingFooter}>Spares: {spareUnits}</Text>
        </View>
      </View>
    </View>
  );
};

const formatPct = (n: number) => `${n.toFixed(1)}%`;

// One module's cost block: the cost lines that sum to its cost, each with its
// derivation note, then a cost / price / margin footer.
const ModuleCostBlock: React.FC<{ row: InternalSplitData['costRows'][number] }> = ({ row }) => (
  <View wrap={false}>
    <Text style={styles.costModuleHeading}>{row.label}</Text>
    {row.lines.map((line, i) => (
      <View key={i}>
        <View style={styles.costLineRow}>
          <Text style={styles.costLineLabel}>{line.label}</Text>
          <Text style={styles.costLineValue}>{formatCurrency(line.cost)}</Text>
        </View>
        {line.note ? <Text style={styles.costLineNote}>{line.note}</Text> : null}
      </View>
    ))}
    <View style={styles.costModuleTotalRow}>
      <Text style={styles.costModuleTotalLabel}>Module cost</Text>
      <Text style={styles.costModuleTotalValue}>{formatCurrency(row.cost)}</Text>
    </View>
    <Text style={styles.costModuleMeta}>
      Price {formatCurrency(row.price)}  ·  Margin {formatPct(row.marginPct)}
    </Text>
  </View>
);

// Internal-only cost summary: every module's pre-margin cost broken into the
// lines that derive it, against the quoted price and implied margin.
const CostSummarySection: React.FC<{ data: InternalSplitData }> = ({ data }) => {
  if (data.costRows.length === 0) return null;
  const blended =
    data.totalModulePrice > 0
      ? ((data.totalModulePrice - data.totalModuleCost) / data.totalModulePrice) * 100
      : 0;
  return (
    <View break>
      <Text style={styles.sectionHeading}>Module Cost Summary (Internal)</Text>
      <View style={styles.summaryBox}>
        {data.costRows.map((row, i) => (
          <View key={i} wrap={false}>
            <ModuleCostBlock row={row} />
          </View>
        ))}

        <View style={styles.costGrandRow}>
          <Text style={styles.costGrandLabel}>All Modules Cost</Text>
          <Text style={styles.costGrandValue}>{formatCurrency(data.totalModuleCost)}</Text>
        </View>
        <Text style={styles.costModuleMeta}>
          Price {formatCurrency(data.totalModulePrice)}  ·  Margin {formatPct(blended)}
        </Text>

        <View style={styles.onboardingNoteRow}>
          <Text style={styles.onboardingNote}>
            Cost is pre-margin internal cost (COGS). Lines without a modelled cost
            (add-ons, gain-share, module floors) are shown at zero margin, so the
            module margin is conservative. Platform &amp; subscription is excluded.
          </Text>
        </View>
      </View>
    </View>
  );
};

interface InternalSplitPDFDocumentProps {
  data: InternalSplitData;
}

export const InternalSplitPDFDocument: React.FC<InternalSplitPDFDocumentProps> = ({ data }) => {
  const addressParts = [
    data.addressLine1,
    data.addressLine2,
    [data.city, data.state, data.postalCode].filter(Boolean).join(', '),
    data.country,
  ].filter(Boolean);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header: repeats on every page, full on page 1, compact afterwards. */}
        <View
          style={styles.headerBand}
          fixed
          render={({ pageNumber }) =>
            pageNumber === 1 ? (
              <>
                <Image src={logo} style={styles.logoImage} />
                <View>
                  <View style={styles.proposalTitleRow}>
                    <Text style={[styles.proposalTitleBase, { color: '#EB1C37' }]}>DATA </Text>
                    <Text style={[styles.proposalTitleBase, { color: '#969696' }]}>360 </Text>
                    <Text style={[styles.proposalTitleBase, { color: TEXT_DARK }]}>PRICING PROPOSAL</Text>
                    <Text style={styles.internalBadge}>INTERNAL</Text>
                  </View>
                  <Text style={styles.proposalMeta}>
                    Date: {data.date}   |   Ref: {data.referenceNumber}
                  </Text>
                </View>
              </>
            ) : (
              <>
                <Image src={logo} style={styles.logoImageSmall} />
                <View style={styles.proposalTitleRow}>
                  <Text style={styles.runningHeaderText}>
                    DATA 360 Pricing Proposal   |   Ref: {data.referenceNumber}
                  </Text>
                  <Text style={styles.internalBadge}>INTERNAL</Text>
                </View>
              </>
            )
          }
        />

        {/* Two-column info */}
        <View style={styles.infoRow}>
          <View style={styles.infoBox}>
            <Text style={styles.sectionHeading}>Prepared For</Text>
            {data.customerName ? <InfoLine label="Customer:" value={data.customerName} /> : null}
            {data.customerAttn ? <InfoLine label="Attention:" value={data.customerAttn} /> : null}
            {data.customerEmail ? <InfoLine label="Email:" value={data.customerEmail} /> : null}
            {data.customerPhone ? <InfoLine label="Phone:" value={data.customerPhone} /> : null}
            {addressParts.map((line, i) => (
              <InfoLine key={i} label={i === 0 ? 'Address:' : ''} value={line} />
            ))}
          </View>
          <View style={styles.infoBoxRight}>
            <Text style={styles.sectionHeading}>Project Details</Text>
            {data.projectTitle ? <InfoLine label="Project:" value={data.projectTitle} /> : null}
            {data.startDate ? <InfoLine label="Start:" value={data.startDate} /> : null}
            {data.endDate ? <InfoLine label="End:" value={data.endDate} /> : null}
            {data.durationLabel ? <InfoLine label="Duration:" value={data.durationLabel} /> : null}
            {data.paymentTerms ? <InfoLine label="Payment:" value={data.paymentTerms} /> : null}
          </View>
        </View>

        {/* Combined price summary: one row per selected product, plus the
            project-level platform & subscription rollup, then the grand total. */}
        <Text style={styles.sectionHeading}>Combined Price Summary</Text>
        <View style={styles.summaryBox}>
          {data.summaryRows.map((row, idx) => (
            <SummaryRow key={idx} label={row.label} value={row.amount} />
          ))}

          <View style={styles.summaryDoubleDivider} />
          <SummaryRow label="GRAND TOTAL" value={data.grandTotal} red />
        </View>

        {/* Per-module one-time vs recurring split (new DATA 360 modules).
            Welding sits after PipeFitter, i.e. just before NDT/Coating. */}
        {(() => {
          const sections = data.moduleSplits.map((module, idx) => (
            <ModuleSplitSection key={`module-${idx}`} module={module} />
          ));
          if (data.weldingMachines.length > 0) {
            const afterWelding = [PRODUCT_DISPLAY_NAMES.ndt, PRODUCT_DISPLAY_NAMES.coating];
            const insertAt = data.moduleSplits.findIndex((m) =>
              afterWelding.includes(m.productName)
            );
            const pos = insertAt === -1 ? sections.length : insertAt;
            sections.splice(pos, 0, <WeldingSplitSection key="welding" data={data} />);
          }
          return sections;
        })()}

        {/* Project-level recurring lines: two-part platform fee and retention */}
        {data.projectLevel && <ProjectLevelSection split={data.projectLevel} />}

        {/* Internal cost-vs-price summary, one row per module */}
        <CostSummarySection data={data} />

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>CRC Evans - Internal Use Only</Text>
          <Text style={styles.footerText}>{data.referenceNumber}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
};

export async function generateInternalSplitPDFBlob(data: InternalSplitData): Promise<Blob> {
  return pdf(<InternalSplitPDFDocument data={data} />).toBlob();
}

export async function downloadInternalSplitPDF(data: InternalSplitData): Promise<void> {
  const blob = await generateInternalSplitPDFBlob(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Internal-Split-${data.referenceNumber}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
